import React, { useRef, useEffect, useMemo, useState } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import styled from "styled-components";

// Error Boundary for Three.js Canvas
class ThreeErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.warn("Three.js Canvas error caught:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Fallback to immediate completion if Three.js fails
      if (this.props.onComplete) {
        setTimeout(() => this.props.onComplete(), 100);
      }
      return null;
    }

    return this.props.children;
  }
}

const SpaceJourneyContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 1000;
  background: radial-gradient(ellipse at center, #0a0a2e 0%, #000000 100%);
`;

// Number component with focus effects
function NumberDisplay({ number, position, opacity = 1 }) {
  const groupRef = useRef();
  const meshRef = useRef();

  useFrame(state => {
    if (groupRef.current) {
      // Simple floating animation
      groupRef.current.position.y =
        position[1] + Math.sin(state.clock.elapsedTime + number) * 0.1;
    }

    // Apply opacity for focus effect
    if (meshRef.current && meshRef.current.material) {
      meshRef.current.material.opacity = opacity;
    }
  });

  // Create HD text texture with caching
  const texture = useMemo(() => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    // Restore HD resolution
    canvas.width = 512;
    canvas.height = 512;

    // Clear
    ctx.fillStyle = "rgba(0,0,0,0)";
    ctx.fillRect(0, 0, 512, 512);

    // Draw number with crisp HD text - no blur
    ctx.shadowColor = "#00ff00";
    ctx.shadowBlur = 20;
    ctx.fillStyle = "#ffffff";
    ctx.font = '700 200px "Chakra Petch", Arial, sans-serif';
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(number.toString(), 256, 256);

    // Add extra glow for depth
    ctx.shadowBlur = 40;
    ctx.fillText(number.toString(), 256, 256);

    const canvasTexture = new THREE.CanvasTexture(canvas);
    canvasTexture.colorSpace = THREE.SRGBColorSpace;
    canvasTexture.generateMipmaps = false;
    canvasTexture.minFilter = THREE.LinearFilter;
    canvasTexture.magFilter = THREE.LinearFilter;
    canvasTexture.wrapS = THREE.ClampToEdgeWrapping;
    canvasTexture.wrapT = THREE.ClampToEdgeWrapping;
    return canvasTexture;
  }, [number]);

  return (
    <group ref={groupRef} position={position}>
      <mesh ref={meshRef}>
        <planeGeometry args={[15, 15]} />
        <meshBasicMaterial
          map={texture}
          transparent={true}
          opacity={opacity}
          side={THREE.DoubleSide}
        />
      </mesh>
    </group>
  );
}

// Optimized static starfield with reduced particles
function SimpleStarField() {
  const starsRef = useRef();

  const { starGeometry, starTexture } = useMemo(() => {
    // Create simple star texture
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    canvas.width = 16;
    canvas.height = 16;

    const centerX = 8;
    const centerY = 8;
    const radius = 6;

    // Simple radial gradient
    const gradient = ctx.createRadialGradient(
      centerX,
      centerY,
      0,
      centerX,
      centerY,
      radius
    );
    gradient.addColorStop(0, "rgba(255, 255, 255, 1)");
    gradient.addColorStop(0.5, "rgba(255, 255, 255, 0.5)");
    gradient.addColorStop(1, "rgba(255, 255, 255, 0)");

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 16, 16);

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.generateMipmaps = false;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;

    // Reduced star count for better performance
    const starCount = 2000;
    const positions = new Float32Array(starCount * 3);
    const sizes = new Float32Array(starCount);
    const originalSizes = new Float32Array(starCount);

    for (let i = 0; i < starCount; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 1000;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 1000;
      positions[i * 3 + 2] = 100 + Math.random() * -800;

      const size =
        Math.random() < 0.1 ? 3 + Math.random() * 2 : 1 + Math.random();
      sizes[i] = size;
      originalSizes[i] = size;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute("size", new THREE.BufferAttribute(sizes, 1));
    geometry.userData = { originalSizes };

    return { starGeometry: geometry, starTexture: texture };
  }, []);

  useFrame(state => {
    if (starsRef.current && state.clock.elapsedTime % 0.5 < 0.016) {
      // Update twinkling less frequently for performance
      const sizes = starsRef.current.geometry.attributes.size.array;
      const originalSizes = starsRef.current.geometry.userData.originalSizes;

      for (let i = 0; i < sizes.length; i += 10) {
        // Skip some stars for performance
        const twinkle = 0.8 + 0.2 * Math.sin(state.clock.elapsedTime * 0.3 + i);
        sizes[i] = originalSizes[i] * twinkle;
      }
      starsRef.current.geometry.attributes.size.needsUpdate = true;
    }
  });

  return (
    <points ref={starsRef} geometry={starGeometry}>
      <pointsMaterial
        size={1.5}
        map={starTexture}
        transparent={true}
        opacity={0.7}
        sizeAttenuation={true}
        alphaTest={0.01}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}

// Camera controller with progressive speed increase
function CameraController({ onComplete, onCameraUpdate }) {
  const { camera } = useThree();
  const startTime = useRef(null);
  const duration = 5000; // 5 seconds
  const startPosition = 50; // Start 50 units ahead of first number
  const totalDistance = 21 * 20 + 50; // Total journey distance (21 even numbers)

  useEffect(() => {
    // Start immediately when component mounts
    startTime.current = Date.now();
  }, []);

  useFrame(() => {
    if (!startTime.current) return;

    const elapsed = Date.now() - startTime.current;
    const timeProgress = Math.min(elapsed / duration, 1);

    if (timeProgress >= 1) {
      if (onComplete) onComplete();
      return;
    }

    // Progressive speed increase: slow start, fast finish
    // Use an easing function that accelerates over time
    const speedProgress = timeProgress * timeProgress * (3 - 2 * timeProgress); // Smooth acceleration

    // Move camera based on accelerated progress
    camera.position.z = startPosition - speedProgress * totalDistance;

    // Camera sway that also increases with speed
    const swayIntensity = 1 + timeProgress * 2; // Sway increases as we go faster
    camera.position.x =
      Math.sin(elapsed * 0.0003 * (1 + timeProgress)) * swayIntensity;
    camera.position.y =
      Math.cos(elapsed * 0.0002 * (1 + timeProgress)) * (swayIntensity * 0.5);

    // Update for starfield sync
    if (onCameraUpdate) {
      onCameraUpdate(Math.abs(camera.position.z) * 10);
    }
  });

  return null;
}

// Main scene
function SimpleScene({ onComplete, onCameraUpdate }) {
  const { camera } = useThree();

  // Generate only even numbers from 2 to 42 positioned further back along the negative Z axis
  const numbers = useMemo(() => {
    const result = [];
    const spacing = 20; // Increased distance between numbers
    let positionIndex = 1; // Track position for spacing

    for (let i = 2; i <= 42; i += 2) {
      // Only even numbers
      result.push({
        number: i,
        position: [
          i === 42 ? 0 : (Math.random() - 0.5) * 8, // Keep 42 centered, others random
          i === 42 ? 0 : (Math.random() - 0.5) * 6, // Keep 42 centered, others random
          -positionIndex * spacing, // Numbers positioned at -20, -40, -60, etc.
        ],
      });
      positionIndex++;
    }
    return result;
  }, []);

  // Find visible numbers with focus and blur effects
  const [visibleNumbers, setVisibleNumbers] = useState([]);

  useFrame(() => {
    const cameraZ = camera.position.z;

    // Get candidates and calculate focus effects
    const candidates = numbers
      .map(num => {
        const relativeZ = num.position[2] - cameraZ;
        const distance = Math.abs(relativeZ);

        // Calculate opacity based on position relative to camera
        let opacity = 1;

        if (relativeZ < 0) {
          // Number is ahead of camera
          if (distance < 30) {
            // Close range - fully visible
            opacity = 1;
          } else if (distance < 70) {
            // Medium distance - slightly faded
            opacity = 0.8;
          } else if (distance < 120) {
            // Far distance - gradual fade-in
            const fadeInProgress = (120 - distance) / 50; // Fade in over 50 units (120 to 70)
            opacity = Math.max(0, 0.4 * Math.pow(fadeInProgress, 2)); // Smooth quadratic fade-in
          } else {
            // Beyond visibility range
            opacity = 0;
          }
        } else {
          // Number is behind camera - fade out quickly
          opacity = Math.max(0, 0.8 - relativeZ * 0.1);
        }

        return {
          ...num,
          distance,
          relativeZ,
          opacity,
        };
      })
      .filter(num => num.opacity > 0.01 && num.distance < 130) // Expanded range for smoother fade-in
      .sort((a, b) => a.distance - b.distance) // Sort by distance (closest first)
      .slice(0, 3); // Always show exactly 3 numbers

    setVisibleNumbers(candidates);
  });

  return (
    <>
      <SimpleStarField />
      <ambientLight intensity={0.5} />
      <CameraController
        onComplete={onComplete}
        onCameraUpdate={onCameraUpdate}
      />

      {visibleNumbers.map(item => (
        <NumberDisplay
          key={item.number}
          number={item.number}
          position={item.position}
          opacity={item.opacity}
        />
      ))}
    </>
  );
}

const SpaceJourneyClean = ({ onComplete, onCameraUpdate }) => {
  return (
    <SpaceJourneyContainer>
      <ThreeErrorBoundary onComplete={onComplete}>
        <Canvas
          camera={{ fov: 75, near: 0.1, far: 1000, position: [0, 0, 0] }}
          gl={{
            antialias: true,
            alpha: true,
            powerPreference: "high-performance",
            stencil: false,
            depth: true,
            premultipliedAlpha: false,
            preserveDrawingBuffer: false,
            failIfMajorPerformanceCaveat: true,
            outputColorSpace: THREE.SRGBColorSpace,
          }}
          dpr={[1, 1.5]}
          performance={{ min: 0.7 }}
          onCreated={({ gl }) => {
            // Ensure color management is properly configured
            gl.toneMapping = THREE.ACESFilmicToneMapping;
            gl.toneMappingExposure = 1;
          }}
        >
          <SimpleScene
            onComplete={onComplete}
            onCameraUpdate={onCameraUpdate}
          />
        </Canvas>
      </ThreeErrorBoundary>
    </SpaceJourneyContainer>
  );
};

export default SpaceJourneyClean;
